import { call, put, select, cancelled } from 'redux-saga/effects';
import { createClientCookies } from 'utils';
import { PRODUCTS_COMPARE_MANAGE } from '../actions';
import { getProductsCompareOrchestratorHost } from 'common/selectors/env';
import { getCustomerID } from 'common/selectors/cookies';
import { transformCompareListsToAPI } from '../utils';
import { api } from 'api';
import {
  validateActionPayload,
  applyProductChangesToGroup,
  handleDeleteListFromServer,
  handleDeleteListFromState,
  handleListCreation,
  handleListUpdate,
  loadProductsData,
  isDataSyncError,
  handleBadRequestError,
  type ManageProductAction,
  type SagaParams,
} from './manage-product';
import { ApiError, CompareActions, type UnifiedCompareState } from '../types';
import { getUnifiedCompareData } from '../selectors/base';
import { CUSTOMER_ID_COOKIE_NAME, YM_UID_COOKIE_NAME } from '../constants';

const ERROR_MESSAGES = {
  MISSING_CUSTOMER_ID: 'Customer ID не найден',
  MISSING_GROUP_ID: 'Group ID не найден',
  INVALID_ACTION: 'Некорректное действие',
} as const;

const EMPTY_PRODUCT_ID = '';
const EMPTY_GROUP_ID = '';

/**
 * NOTE: Главная сага для управления продуктами в списке сравнения
 * Обрабатывает добавление, удаление продуктов и удаление групп
 * Синхронизирует изменения с API и локальным состоянием
 */
export function* manageProductInCompareList(action: ManageProductAction): Generator {
  const abortController = yield call([api, 'abortController']);
  const currentState = yield select(getUnifiedCompareData);
  const clientSideCookie = createClientCookies();

  try {
    if (!validateActionPayload(action.payload)) {
      throw new Error(ERROR_MESSAGES.INVALID_ACTION);
    }

    const { productId, familyId, groupName = '', withData = false, productIds } = action.payload;
    const groupId = familyId || '';

    if (!groupId) {
      throw new Error(ERROR_MESSAGES.MISSING_GROUP_ID);
    }

    const customerIdServerCookie = yield select(getCustomerID);
    const customerIdClientCookie =
      clientSideCookie.get(CUSTOMER_ID_COOKIE_NAME) || clientSideCookie.get(YM_UID_COOKIE_NAME);
    const customerId = customerIdServerCookie || customerIdClientCookie;
    if (!customerId) {
      throw new Error(ERROR_MESSAGES.MISSING_CUSTOMER_ID);
    }

    const host: string = yield select(getProductsCompareOrchestratorHost);

    const existingGroup = currentState[groupId];

    const currentProductIds = existingGroup ? Object.keys(existingGroup.productsData) : [];
    const updatedProductIds = applyProductChangesToGroup(
      currentProductIds,
      action.payload.action,
      productId,
      productIds,
    );

    const sagaParams: SagaParams = {
      customerId,
      host,
      abortController,
      currentLists: transformCompareListsToAPI(currentState),
      productId,
      groupId,
    };

    const shouldDeleteList =
      updatedProductIds.length === 0 || action.payload.action === CompareActions.DELETE_GROUP;
    const existsOnServer = existingGroup?.isSyncedWithServer ?? false;

    if (shouldDeleteList && existsOnServer) {
      yield* handleDeleteListFromServer(sagaParams);
    } else if (shouldDeleteList) {
      yield* handleDeleteListFromState(sagaParams, currentState);
    } else if (existsOnServer) {
      yield* handleListUpdate(sagaParams, groupName, updatedProductIds);
    } else {
      yield* handleListCreation(sagaParams, groupName, updatedProductIds);
    }

    if (withData && updatedProductIds.length > 0) {
      yield* loadProductsData(updatedProductIds);
    }
  } catch (error) {
    yield* handleManageProductError(error, action, currentState);
  } finally {
    yield* cancelManageProductRequest(abortController);
  }
}

function* handleManageProductError(
  error: unknown,
  action: ManageProductAction,
  currentState: UnifiedCompareState,
): Generator {
  const err = error instanceof Error ? error : new Error(`Неизвестная ошибка: ${String(error)}`);

  if (isDataSyncError(error as ApiError)) {
    yield* handleBadRequestError();
  }

  const errorState = {
    error: err,
    productId: action.payload?.productId || EMPTY_PRODUCT_ID,
    groupId: action.payload?.familyId || EMPTY_GROUP_ID,
    currentState,
  };

  yield put(PRODUCTS_COMPARE_MANAGE.failure(errorState));
  // eslint-disable-next-line no-console
  console.error('Ошибка управления продуктом в списке сравнения:', {
    message: err.message,
    stack: err.stack,
    action: action.payload?.action,
    productId: action.payload?.productId,
    groupId: action.payload?.familyId,
    timestamp: new Date().toISOString(),
  });
}

function* cancelManageProductRequest(abortController: { cancel: () => void }): Generator {
  if ((yield cancelled()) as boolean) {
    abortController.cancel();
  }
}
